import type { AccessPermissions } from '@/features/access';

/**
 * The header navigation registry. A slice joins the header by adding one
 * `NavGroup` here, plus its routes in `routes.tsx`.
 *
 * It lives next to the route table and is plain data on purpose: slices do not
 * export nav entries from their barrels, because the lazy header would then
 * import the whole slice (every page and every GraphQL document) into the
 * header chunk. The type-only import above is erased at build.
 */
/**
 * What the header knows about the signed-in account: the permissions, plus
 * whether it is linked to at least one API client (the Developer item).
 */
export interface NavAccess extends AccessPermissions {
    ownsApiClients: boolean;
}

/**
 * `A` is what an item's predicate reads. It defaults to everything the header
 * knows (`NavAccess`); a group whose predicates read less (the admin sections,
 * which `AdminArea` also reads) is typed with the narrower `AccessPermissions`
 * and still fits the registry, because a predicate that needs less accepts more.
 */
export interface NavItem<A = NavAccess> {
    id: string;
    label: string;
    path: string;
    /** Each item owns its predicate, so a Developer item can differ. */
    canAccess(access: A): boolean;
}

export interface NavGroup<A = NavAccess> {
    id: string;
    label: string;
    /**
     * The group's name is only for assistive tech (`sr-only`). For a label the
     * bar already shows: a group whose logo already names the app
     * ("Dawn Patrol Developer" over "API clients"), or Admin, which the admin
     * logo spells out.
     */
    labelHidden?: boolean;
    items: readonly NavItem<A>[];
}

export const NAV_COPY = {
    mainLabel: 'Main',
    signOut: 'Sign out',
    accountMenu: 'Account menu',
    apps: 'Apps',
    appearance: 'Appearance',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',
    /** Read after the current section's name on the narrow picker. */
    changeSection: ', change section',
} as const;

const adminOnly = (permissions: AccessPermissions) => permissions.isAdmin;

/** The admin sections, in nav order. */
export const ADMIN_NAV_GROUP: NavGroup<AccessPermissions> = {
    id: 'admin',
    label: 'Admin',
    // The admin logo already says Admin; the label stays as the list's name.
    labelHidden: true,
    items: [
        {
            id: 'users',
            label: 'Users',
            path: '/admin/users',
            canAccess: adminOnly,
        },
        {
            id: 'spots',
            label: 'Spots',
            path: '/admin/spots',
            canAccess: adminOnly,
        },
        {
            id: 'subscriptions',
            label: 'Subscriptions',
            path: '/admin/subscriptions',
            canAccess: adminOnly,
        },
        {
            id: 'challenges',
            label: 'Challenges',
            path: '/admin/challenges',
            canAccess: adminOnly,
        },
        {
            id: 'api-clients',
            label: 'API clients',
            path: '/admin/api-clients',
            canAccess: adminOnly,
        },
    ],
};

/**
 * The Developer portal, shown only to an account linked to an API client. Any
 * signed-in account may still open `/developer` (and gets the empty state);
 * this only decides whether the header advertises it.
 */
export const DEVELOPER_NAV_GROUP: NavGroup = {
    id: 'developer',
    label: 'Developer',
    labelHidden: true,
    items: [
        {
            id: 'developer',
            label: 'API clients',
            path: '/developer',
            canAccess: (access) => access.ownsApiClients,
        },
    ],
};

export const NAV_GROUPS: readonly NavGroup[] = [
    ADMIN_NAV_GROUP,
    DEVELOPER_NAV_GROUP,
];

/** The groups the user may see: items filtered, empty groups dropped. */
export function accessibleGroups<A>(
    groups: readonly NavGroup<A>[],
    access: A
): NavGroup<A>[] {
    return groups
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => item.canAccess(access)),
        }))
        .filter((group) => group.items.length > 0);
}
